import { Editor } from '@tinymce/tinymce-react';
import { Dispatch, SetStateAction } from 'react';
import axios from 'axios';
import { Box } from '@mui/material';

const uploadFileUrl = 'pvt/bucket/upload-file-to-cloud-storage';

let tinyMceConfig: { baseUrl?: string; apiKey?: string } = {};

export function configureTinyMce(config: { baseUrl?: string; apiKey?: string }) {
  tinyMceConfig = config;
  try {
    (instanceAxios as any).defaults.baseURL = getApiBaseUrl();
  } catch (e) {
    console.log(e);
  }
}

function getApiBaseUrl(): string {
  let url: string | undefined =
    tinyMceConfig.baseUrl ||
    (typeof process !== 'undefined' && process.env
      ? process.env?.['NEXT_PUBLIC_API_URL'] || process.env?.['API_URL']
      : undefined);
  if (url) {
    if (!url.includes('http')) {
      const protocol = url.includes('localhost') ? 'http' : 'https';
      url = `${protocol}://${url}`;
    }
    if (!url.endsWith('/')) {
      url = `${url}/`;
    }
    if (!url.endsWith('api/')) {
      url = `${url}api/`;
    }
    return url;
  }
  return '';
}

const instanceAxios = axios.create({
  baseURL: getApiBaseUrl()
});

instanceAxios.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config!.headers!['Authorization'] = token;
  }
  return config;
});

export function EditorComponent({
  setValue,
  value,
  height,
  initialValue,
  isMedia = true,
  placeholder
}: {
  value: string | undefined;
  setValue: ((content: string) => void) | Dispatch<SetStateAction<string | undefined>>;
  height?: number;
  initialValue?: string;
  isMedia?: boolean;
  placeholder?: string;
}) {
  return (
    <Editor
      tinymceScriptSrc="/tinymce/tinymce.min.js"
      apiKey={tinyMceConfig.apiKey || (typeof process !== 'undefined' && process.env?.['NEXT_PUBLIC_TINY_KEY']) || ''}
      value={value}
      onEditorChange={editorContent => {
        setValue(editorContent);
      }}
      init={{
        height: height || 400,
        placeholder,
        convert_urls: false,
        language: 'he_IL',
        language_url: '/langs/he_IL.js',
        directionality: 'rtl',
        menubar: 'favs file edit view insert format tools table help',
        plugins: [
          'advlist',
          'autolink',
          'lists',
          'link',
          'image',
          'charmap',
          'preview',
          'anchor',
          'searchreplace',
          'visualblocks',
          'code',
          'fullscreen',
          'insertdatetime',
          'media',
          'table',
          'code',
          'help',
          'wordcount'
        ],
        setup: function (editor) {
          editor.ui.registry.addButton('customimage', {
            icon: 'image',
            tooltip: 'הוסף תמונה או קובץ',
            onAction: function () {
              const input = document.createElement('input');
              input.setAttribute('type', 'file');
              input.setAttribute('accept', 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt');

              input.onchange = async function () {
                const file = input.files?.[0];
                if (!file) return;

                if (file.type.startsWith('video/') || file.type.startsWith('audio/')) {
                  alert('לא ניתן להעלות וידאו/אודיו מכפתור התמונות');
                  return;
                }

                if (file.size > 5 * 1024 * 1024) {
                  alert('הקובץ גדול מדי. ניתן לעלות קובץ עד 5MB בלבד');
                  return;
                }

                const formData = new FormData();
                formData.append('file', file);

                try {
                  const { data } = await instanceAxios.post(uploadFileUrl, formData);
                  const url = data?.publicUrl;
                  if (!url) return;

                  if (file.type.startsWith('image/')) {
                    editor.insertContent(`<img src="${url}" alt="${file.name}" />`);
                  } else {
                    editor.insertContent(`<a href="${url}" target="_blank" rel="noopener noreferrer">${file.name}</a>`);
                  }
                } catch (error) {
                  alert('שגיאה בעת העלאת הקובץ');
                }
              };

              input.click();
            }
          });

          editor.ui.registry.addButton('custommedia', {
            icon: 'embed',
            tooltip: 'הוסף וידאו או אודיו',
            onAction: function () {
              // יצירת input עם הגבלות לוידאו/אודיו בלבד
              const input = document.createElement('input');
              input.setAttribute('type', 'file');
              input.setAttribute('accept', 'video/*,audio/*');

              input.onchange = async function () {
                const file = input.files?.[0];
                if (!file) return;

                // בדיקת סוג הקובץ
                const isMedia = file.type.startsWith('video/') || file.type.startsWith('audio/');

                if (!isMedia) {
                  alert('ניתן להעלות רק קבצי וידאו או אודיו');
                  return;
                }

                // העלאת הקובץ
                const formData = new FormData();
                formData.append('file', file);

                try {
                  const { data } = await instanceAxios.post(uploadFileUrl, formData);

                  // הוספת הקובץ לעורך
                  const mediaHtml = file.type.startsWith('video/')
                    ? `<video controls><source src="${data?.publicUrl}" type="${file.type}"></video>`
                    : `<audio controls><source src="${data?.publicUrl}" type="${file.type}"></audio>`;

                  editor.insertContent(mediaHtml);
                } catch (error) {
                  alert('שגיאה בעת העלאת הקובץ');
                }
              };

              input.click();
            }
          });
        },
        image_uploadtab: true,
        image_description: false,
        content_style: 'img { max-width: 100%; height: auto; }',
        images_upload_handler: async blobInfo => {
          const formData = new FormData();
          formData.append('file', blobInfo.blob(), blobInfo.filename());
          try {
            const { data } = await instanceAxios.post(uploadFileUrl, formData);
            return data?.publicUrl;
          } catch (error) {
            throw new Error('שגיאה בעת טעינת הקובץ ניתן לעלות קובץ עד 5mb בלבד');
          }
        },
        file_picker_types: 'file image media',
        file_picker_callback: (callback, value, meta) => {
          const input = document.createElement('input');
          input.setAttribute('type', 'file');

          if (meta['filetype'] === 'image') {
            input.setAttribute('accept', 'image/*');
          } else if (meta['filetype'] === 'media') {
            input.setAttribute('accept', 'video/*,audio/*');
          } else {
            input.setAttribute('accept', '.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar');
          }

          input.onchange = async function () {
            const file = input.files?.[0];
            if (!file) return;

            if (file.size > 5 * 1024 * 1024) {
              alert('הקובץ גדול מדי. ניתן לעלות קובץ עד 5MB בלבד');
              return;
            }

            const formData = new FormData();
            formData.append('file', file);

            try {
              const { data } = await instanceAxios.post(uploadFileUrl, formData);

              callback(data?.publicUrl, {
                text: file.name,
                title: file.name
              });
            } catch (error) {
              alert('שגיאה בעת העלאת הקובץ');
            }
          };

          input.click();
        },
        toolbar:
          `${isMedia ? 'customimage | custommedia |' : ''} link | undo redo | blocks | ` +
          'bold italic forecolor | alignleft aligncenter ' +
          'alignright alignjustify | bullist numlist outdent indent | ' +
          'removeformat | autolink | help '
      }}
    />
  );
}

export const ViewRichText = ({ value, maxHeight }: { value: string; maxHeight?: number }) => {
  return (
    <Box
      sx={{
        border: '1px solid #eee',
        borderRadius: 1,
        px: 2,
        minHeight: 100,
        background: '#fafafa',
        ...(maxHeight ? { maxHeight, overflow: 'auto' } : {})
      }}
    >
      <div dangerouslySetInnerHTML={{ __html: value || '' }} />
    </Box>
  );
};
